/**
 * The calm full-page message a client sees instead of a studio that is not switched on for the team, while Beatly
 * has paused every client, or when the app could not check access (or read the AI terms) just now. Never an error,
 * never a dead end: try again, or one way back home.
 */
import { PauseCircle, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const STUDIO_CLOSED_TITLE = "This studio isn't switched on for your team.";
export const STUDIO_PAUSED_TITLE = 'The studios are paused for now.';
export const STUDIO_UNREACHABLE_TITLE = "We couldn't open this studio just now.";
export const STUDIO_UNREACHABLE_BODY = 'Try again in a moment.';
export const ASK_BEATLY = 'Ask your Beatly contact.';

export type StudioUnavailableState = 'closed' | 'paused' | 'unreachable';

export function StudioUnavailable({ state, studioName, onHome, onRetry }: { state: StudioUnavailableState; studioName?: string; onHome?: () => void; onRetry?: () => void }) {
  const Icon = state === 'paused' ? PauseCircle : state === 'unreachable' ? RefreshCw : Lock;
  const title = state === 'paused' ? STUDIO_PAUSED_TITLE : state === 'unreachable' ? STUDIO_UNREACHABLE_TITLE : STUDIO_CLOSED_TITLE;
  return (
    <div role="status" data-testid={`studio-${state}`} className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-3 px-4 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#f2f4f7]"><Icon className="h-5 w-5 text-[#667085]" aria-hidden /></span>
      {studioName ? <p className="text-[13px] text-[#667085]">{studioName}</p> : null}
      <h1 className="text-[18px] text-[#101828]" style={{ fontWeight: 600 }}>{title}</h1>
      <p className="text-[14px] leading-5 text-[#667085]">{state === 'unreachable' ? STUDIO_UNREACHABLE_BODY : ASK_BEATLY}</p>
      <div className="flex items-center gap-2">
        {state === 'unreachable' && onRetry ? <Button onClick={onRetry}>Try again</Button> : null}
        {onHome ? <Button variant="outline" onClick={onHome}>Go to Home</Button> : null}
      </div>
    </div>
  );
}

/** One calm line above a page that stays usable while Beatly has paused every client. */
export function ClientsPausedBanner() {
  return (
    <div role="status" data-testid="clients-paused-banner" className="flex items-center gap-2 border-b border-[#eaecf0] bg-[#f9fafb] px-4 py-2 text-[13px] text-[#475467]">
      <PauseCircle className="h-4 w-4 shrink-0 text-[#667085]" aria-hidden />
      <span>{STUDIO_PAUSED_TITLE} {ASK_BEATLY}</span>
    </div>
  );
}
